import React, { useRef, useState } from 'react';
import { CaptureOverlay } from '../shared';
import type { ScreenMode } from './useScreenRecorder';

interface ScreenReviewProps {
  taskName: string;
  videoUrl: string | null;
  duration: number;
  mode: ScreenMode;
  onDelete: () => void;
  onRecordAnother: () => void;
  onContinue: () => void;
  onClose: () => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const ScreenReview: React.FC<ScreenReviewProps> = ({
  taskName,
  videoUrl,
  duration,
  mode,
  onDelete,
  onRecordAnother,
  onContinue,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current || !videoUrl) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const badgeLabel = mode === 'screen-cam' ? 'SCREEN + CAM' : 'SCREEN';

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <div className="co-sheet">
        <div className="co-sheet-handle" />
        <div className="co-sheet-head">
          <div className="co-sheet-title">Review Recording</div>
          <div className="co-sheet-badge">1 take</div>
        </div>

        <div className="co-sheet-body">
          {/* Video card */}
          <div className="sc-video-card">
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                onEnded={() => setIsPlaying(false)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            <div className="sc-video-play" onClick={togglePlay}>
              {isPlaying ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              )}
            </div>
            <div className="sc-video-dur">{formatTime(duration)}</div>
            <div className="sc-video-badge">{badgeLabel}</div>
            <button className="sc-video-del" onClick={onDelete}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="sc-review-hint">Tap to preview full screen</div>
          <button className="co-btn-ghost" onClick={onRecordAnother}>+ Record another take</button>
        </div>

        <div className="co-sheet-actions">
          <button className="co-btn-sage" onClick={onContinue}>Continue to Notes →</button>
        </div>
      </div>
    </CaptureOverlay>
  );
};
